<script setup>
import { ref, computed, inject } from "vue";
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
} from "@headlessui/vue";
import {
  SearchIcon,
  XIcon,
  ChevronDoubleRightIcon,
  VideoCameraIcon,
  BookOpenIcon,
  CollectionIcon,
  ViewListIcon,
  UserIcon,
  FolderIcon,
  TagIcon,
  SpeakerphoneIcon,
} from "@heroicons/vue/outline";
import { useRouter } from "vue-router";
import { debounce as _debounce } from "lodash-es";
import Bugsnag from "@bugsnag/js";
import { useFileManagerStore } from "@/stores/filemanager";

const axios = inject("axios");
const router = useRouter();
const fileManagerStore = useFileManagerStore();

const isOpen = ref(false);
const search = ref(null);
const isSearching = ref(false);
const selectedCategory = ref(null);
const activeFilter = ref("all");

// Use results object instead of individual refs
const results = ref({
  units: [],
  sub_modules: [],
  modules: [],
  users: [],
  playlists: [],
  groups: [],
  files: [],
  categories: [],
  news_feeds: [],
});

// Individual loading states for each filter type
const loadingStates = ref({
  videos: false,
  courses: false,
  categories: false,
  modules: false,
  users: false,
  playlists: false,
  "resource manager": false,
  groups: false,
  "news feed": false,
});

// Map filter names to result keys
const filterToResultKey = {
  videos: "units",
  courses: "modules",
  categories: "categories",
  modules: "sub_modules",
  users: "users",
  playlists: "playlists",
  "resource manager": "files",
  groups: "groups",
  "news feed": "news_feeds",
};

const items = [
  "all",
  "courses",
  "categories",
  "modules",
  "videos",
  "users",
  "playlists",
  "resource manager",
  "groups",
  "news feed",
];

// Section metadata driving the results list — replaces the previous
// nine near-identical, hand-written result blocks in the template.
const sections = [
  {
    key: "videos",
    label: "Videos",
    resultKey: "units",
    icon: VideoCameraIcon,
    emptyLabel: "No unit(s) found",
    getLabel: (u) => u.name,
    getHref: (u) => `/units/${u.id}`,
  },
  {
    key: "courses",
    label: "Course",
    resultKey: "modules",
    icon: BookOpenIcon,
    emptyLabel: "No course(s) found",
    getLabel: (m) => m.name,
    getHref: (m) => `/units/${m.unit_display.id}`,
  },
  {
    key: "modules",
    label: "Modules",
    resultKey: "sub_modules",
    icon: CollectionIcon,
    emptyLabel: "No module(s) found",
    getLabel: (m) => m.name,
    getHref: (m) =>
      `/units/${m.first_unit ? m.first_unit.id : m.unit_display?.id}`,
  },
  {
    key: "playlists",
    label: "Playlists",
    resultKey: "playlists",
    icon: ViewListIcon,
    emptyLabel: "No playlist(s) found",
    getLabel: (p) => p.name,
    getHref: (p) =>
      `/units/${p.first_unit ? p.first_unit.id : p.unit_display?.id}/${p.id}`,
  },
  {
    key: "users",
    label: "Users",
    resultKey: "users",
    icon: UserIcon,
    emptyLabel: "No user(s) found",
    getLabel: (u) => u.name,
    getHref: (u) => `/reports?tab=course-overview&userId=${u?.id}`,
  },
  {
    key: "resource manager",
    label: "Resource Manager",
    resultKey: "files",
    icon: FolderIcon,
    emptyLabel: "No Resource Found",
    getLabel: (f) => f.name,
    onClick: (file) => handleFileClick(file),
  },
  {
    key: "categories",
    label: "Categories",
    resultKey: "categories",
    icon: TagIcon,
    emptyLabel: "No unit(s) or course(s) found.",
    getLabel: (c) => c.name,
    getHref: (c) => `${c?.link}`,
  },
  {
    key: "news feed",
    label: "News Feed",
    resultKey: "news_feeds",
    icon: SpeakerphoneIcon,
    emptyLabel: "No news feed(s) found",
    getLabel: (n) => n.title,
    onClick: (newsFeed) => handleNewsFeedClick(newsFeed),
  },
];

// Only surface filter chips that map to a rendered section.
const chips = items.filter(
  (item) => item === "all" || sections.some((section) => section.key === item)
);

const visibleSections = computed(() =>
  activeFilter.value === "all"
    ? sections
    : sections.filter((section) => section.key === activeFilter.value)
);

const hasAnyResults = computed(() =>
  sections.some((section) => results.value[section.resultKey]?.length)
);

const isAnyLoading = computed(() =>
  Object.values(loadingStates.value).some(Boolean)
);

const closeModal = () => {
  isOpen.value = false;
};
const openModal = () => {
  isOpen.value = true;
};

const clearSearch = () => {
  search.value = "";
  globalSearch();
};

// Make API call for a specific filter type
const searchByFilter = async (filterType, searchQuery = "") => {
  if (!searchQuery.length && !selectedCategory.value) {
    return;
  }

  const resultKey = filterToResultKey[filterType];
  if (!resultKey) return;

  // Clear previous results for this filter type
  results.value[resultKey] = [];
  loadingStates.value[filterType] = true;

  try {
    const { data } = await axios.get("/api/search", {
      params: {
        search_query: searchQuery,
        filter: filterType,
        selectedCategory: selectedCategory.value,
      },
    });

    // Update only the specific result type
    if (data.results && data.results[resultKey]) {
      results.value[resultKey] = data.results[resultKey];
    } else {
      results.value[resultKey] = [];
    }
  } catch (error) {
    Bugsnag.notify(error);
    results.value[resultKey] = [];
  } finally {
    loadingStates.value[filterType] = false;
  }
};

const globalSearch = _debounce(async () => {
  if (!search.value?.length && !selectedCategory.value) {
    isSearching.value = false;
    // Clear all results
    Object.keys(results.value).forEach((key) => {
      results.value[key] = [];
    });
    return;
  }

  isSearching.value = true;

  // Make parallel API calls for all filter types
  const filterTypes = items.filter((item) => item !== "all");
  await Promise.all(
    filterTypes.map((filterType) => searchByFilter(filterType, search.value))
  );
}, 1000);

const handleFileClick = (file) => {
  if (file.type == "folder") {
    router.push({
      path: `/resource-manager/${file.id}`,
    });

    closeModal();
    return;
  }

  closeModal();
  fileManagerStore.selectedFile = file;
  fileManagerStore.isViewSelectedFile = true;
  router.push({
    path: "/resource-manager",
    query: { id: file?.id },
  });
};

const handleNewsFeedClick = (newsFeed) => {
  closeModal();
  router.push({
    name: "Announcement Details",
    params: { id: newsFeed.id },
  });
};

const onSectionItemClick = (event, section, item) => {
  if (!section.onClick) return;
  event.preventDefault();
  section.onClick(item);
};

const redirectToSearch = () => {
  globalSearch.cancel();
  router.push({
    name: "search",
    query: {
      search: search.value,
    },
  });

  closeModal();
};
</script>

<template>
  <div>
    <div>
      <SearchIcon
        class="h-6 w-6 text-untitled-gray-400 hover:cursor-pointer"
        @click="openModal"
      />
    </div>
    <TransitionRoot appear :show="isOpen" as="template">
      <Dialog as="div" @close="closeModal" class="relative z-[9999999]">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-untitled-gray-500 bg-opacity-40 backdrop-blur-sm" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-start justify-center p-4 pt-[10vh] text-center">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel
                class="w-full max-w-2xl transform rounded-xl bg-white text-left align-middle shadow-2xl transition-all overflow-hidden flex flex-col max-h-[70vh]"
              >
                <div class="flex items-center gap-3 px-4 border-b border-untitled-gray-200 shrink-0">
                  <SearchIcon class="h-5 w-5 text-untitled-gray-400 shrink-0" />
                  <input
                    v-model="search"
                    @input="globalSearch"
                    @keypress.enter="redirectToSearch"
                    class="flex-1 border-0 py-4 text-untitled-gray-900 placeholder-untitled-gray-400 focus:outline-none focus:ring-0 text-base"
                    type="text"
                    placeholder="Search videos, courses, users, files..."
                    autofocus
                  />
                  <button
                    v-if="search?.length"
                    type="button"
                    class="text-untitled-gray-400 hover:text-untitled-gray-600 shrink-0"
                    @click="clearSearch"
                  >
                    <XIcon class="h-4 w-4" />
                  </button>
                  <kbd
                    v-if="search?.length"
                    class="hidden sm:inline-flex items-center rounded border border-untitled-gray-200 px-1.5 py-0.5 text-xs text-untitled-gray-400 shrink-0"
                    @click="redirectToSearch"
                    >Enter</kbd
                  >
                </div>

                <div
                  v-if="isSearching && search?.length"
                  class="thin-scrollbar flex items-center gap-2 px-4 py-2 border-b border-untitled-gray-100 overflow-x-auto shrink-0"
                >
                  <button
                    v-for="chip in chips"
                    :key="chip"
                    type="button"
                    class="whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors"
                    :class="
                      activeFilter === chip
                        ? 'bg-emerald-700 text-white'
                        : 'bg-untitled-gray-100 text-untitled-gray-600 hover:bg-untitled-gray-200'
                    "
                    @click="activeFilter = chip"
                  >
                    {{ chip }}
                  </button>
                </div>

                <div class="overflow-y-auto px-2 py-2" v-if="isSearching && search?.length">
                  <div
                    v-if="!isAnyLoading && !hasAnyResults"
                    class="flex flex-col items-center justify-center gap-1 py-14 text-untitled-gray-400"
                  >
                    <SearchIcon class="h-8 w-8 mb-1" />
                    <p class="text-sm font-medium text-untitled-gray-500">
                      No results found for &ldquo;{{ search }}&rdquo;
                    </p>
                    <p class="text-xs">Try a different keyword or check your spelling.</p>
                  </div>

                  <template v-for="section in visibleSections" :key="section.key">
                    <div
                      v-if="
                        loadingStates[section.key] ||
                        results[section.resultKey]?.length ||
                        activeFilter === section.key
                      "
                      class="px-2 py-3"
                    >
                      <div class="flex items-center gap-2 mb-1.5">
                        <component :is="section.icon" class="h-4 w-4 text-untitled-gray-400" />
                        <h3 class="text-xs font-semibold uppercase tracking-wide text-untitled-gray-500">
                          {{ section.label }}
                        </h3>
                        <span
                          v-if="results[section.resultKey]?.length"
                          class="text-xs text-untitled-gray-400"
                        >
                          {{ results[section.resultKey].length }}
                        </span>
                      </div>

                      <div
                        v-if="loadingStates[section.key]"
                        class="px-2 py-2 text-sm text-untitled-gray-400"
                      >
                        Loading...
                      </div>

                      <ul v-else-if="results[section.resultKey]?.length" class="space-y-0.5">
                        <li v-for="item in results[section.resultKey].slice(0, 5)" :key="item.id">
                          <a
                            class="group flex items-center justify-between gap-3 rounded-lg px-2 py-2 text-sm text-untitled-gray-600 hover:bg-untitled-gray-50 hover:text-untitled-gray-900 cursor-pointer"
                            :href="section.getHref ? section.getHref(item) : undefined"
                            @click="onSectionItemClick($event, section, item)"
                          >
                            <span class="truncate">{{ section.getLabel(item) }}</span>
                            <ChevronDoubleRightIcon
                              class="h-4 w-4 shrink-0 text-untitled-gray-300 opacity-0 group-hover:opacity-100 transition-opacity"
                            />
                          </a>
                        </li>
                        <li
                          v-if="results[section.resultKey].length > 5"
                          class="px-2 pt-1 text-xs font-medium text-untitled-gray-400"
                        >
                          Press enter for more results
                        </li>
                      </ul>

                      <p v-else class="px-2 py-2 text-sm text-untitled-gray-400">
                        {{ section.emptyLabel }}
                      </p>
                    </div>
                  </template>
                </div>

                <div
                  v-if="!search?.length"
                  class="flex items-center justify-center gap-1 py-14 text-sm text-untitled-gray-400"
                >
                  Start typing to search across the platform
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<style scoped>
.thin-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: theme("colors.untitled-gray.300") transparent;
}

.thin-scrollbar::-webkit-scrollbar {
  height: 4px;
}

.thin-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.thin-scrollbar::-webkit-scrollbar-thumb {
  background-color: theme("colors.untitled-gray.300");
  border-radius: 9999px;
}
</style>
